import { apiFetch } from './client'
import { cached, invalidate, invalidatePrefix } from './cache'
import { Payment } from './payments'

export interface Membership {
  id: string
  user_id: string
  plan_id: string
  status: string
  start_date: string
  end_date: string
  auto_renew: boolean
  notes?: string
  created_at: string
}

export interface MembershipWithPlan extends Membership {
  plan_name: string
  duration_days: number
  grace_days: number
}

export interface CreateMembershipInput {
  userId: string
  planId: string
  startDate?: string
  endDate?: string
  autoRenew?: boolean
  notes?: string
  paymentMethodId?: string
  renew?: boolean        // expire the current active membership before creating this one
}

export interface UpdateMembershipInput {
  planId?: string
  startDate?: string
  endDate?: string
  status?: 'active' | 'expired' | 'suspended' | 'cancelled'
  autoRenew?: boolean
  notes?: string
}

export interface MembershipsPage {
  memberships: Membership[]
  total: number
  limit: number
  offset: number
}

export const listMemberships = (userId?: string, limit = 100, offset = 0): Promise<MembershipsPage> => {
  if (userId) {
    const params = new URLSearchParams({ userId, limit: String(limit), offset: String(offset) })
    return apiFetch<MembershipsPage>(`/memberships?${params}`)
  }
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })
  return cached<MembershipsPage>(
    `memberships-overview:${params}`,
    () => apiFetch<MembershipsPage>(`/memberships?${params}`),
    300_000,
  )
}

export const getMemberships = (userId: string) =>
  apiFetch<{ memberships: Membership[] }>(`/memberships?userId=${userId}`)

export const getMembership = (id: string) =>
  apiFetch<{ membership: Membership }>(`/memberships/${id}`)

export const findActiveMembership = (userId: string) =>
  apiFetch<{ membership: MembershipWithPlan | null }>(
    `/memberships?userId=${userId}&active=true`
  )

export const createMembership = async (data: CreateMembershipInput) => {
  const r = await apiFetch<{ membership: Membership; payment: Payment }>('/memberships', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  invalidatePrefix('memberships-overview:')
  return r
}

export const updateMembership = async (id: string, data: UpdateMembershipInput) => {
  const r = await apiFetch<{ membership: Membership }>(`/memberships/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
  invalidatePrefix('memberships-overview:')
  return r
}

export const deleteMembership = async (id: string) => {
  const r = await apiFetch<{ ok: boolean }>(`/memberships/${id}`, { method: 'DELETE' })
  invalidatePrefix('memberships-overview:')
  return r
}

export const expireOverdueMemberships = async () => {
  const r = await apiFetch<{ expired: number }>('/memberships/expire-overdue', { method: 'POST' })
  invalidatePrefix('memberships-overview:')
  return r
}

export const getExpiringToday = () =>
  apiFetch<{ memberships: Membership[] }>('/memberships/expiring-today')
